"use client";

import { getSearchResults } from "@/app/api/client-only";
import { SearchResult } from "@/types/journal";
import { useSearchParams } from "next/navigation";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

export interface FilterOptions {
  imprint?: string[];
  payment?: string[];
  licenses?: string[];
  publishing_model?: string[];
}

interface SearchContextType {
  slug: string;
  searchTerm: string | null;
  setSearchTerm: (term: string | null) => void;
  currentSearchTerm: string | null;
  filters: FilterOptions;
  searchResult: SearchResult | null;
  setSearchResult: (result: SearchResult | null) => void;
  loading: boolean;
  search: (
    searchTerm: string,
    filters: FilterOptions,
    resetFilters?: boolean,
    skip?: number
  ) => Promise<void>;
  handleFilterChange: (key: keyof FilterOptions, value: string) => void;
  originalFilters: FilterOptions | null;
  resetFilters: () => void;
}

const SearchContext = createContext<SearchContextType | undefined>(undefined);

export function SearchProvider({
  slug,
  children,
}: {
  children: ReactNode;
  slug: string;
}) {
  const searchParams = useSearchParams();
  const [shouldLoad, setShouldLoad] = useState(!!searchParams.get("term"));

  const [searchTerm, setSearchTerm] = useState<string | null>(
    searchParams.get("term")
  );
  const currentSearchTerm = searchParams.get("term");
  const [searchResult, setSearchResult] = useState<SearchResult | null>(null);

  const [originalFilters, setOriginalFilters] = useState<FilterOptions | null>(
    null
  );

  const resetFilters = useCallback(() => {
    setFilters({});
    setOriginalFilters(null);
  }, []);

  const [filters, setFilters] = useState<FilterOptions>({
    imprint: searchParams.getAll("imprint"),
    payment: searchParams.getAll("payment"),
    licenses: searchParams.getAll("licenses"),
    publishing_model: searchParams.getAll("publishing_model"),
  });
  const [loading, setLoading] = useState(false);

  const search = useCallback(
    async (
      searchTerm: string,
      activeFilters: FilterOptions,
      resetFilters?: boolean,
      skip?: number
    ) => {
      setLoading(true);
      const result = await getSearchResults(
        slug,
        searchTerm ?? "",
        activeFilters,
        skip
      );
      if (resetFilters) {
        setFilters({});
      }
      if (resetFilters && result?.filters) {
        const { imprints, payments, licenses, publishing_models } =
          result.filters;
        const filters: FilterOptions = {
          imprint: imprints.sort(),
          payment: payments.sort(),
          licenses: licenses.sort(),
          publishing_model: publishing_models.sort(),
        };
        setOriginalFilters(filters);
      } else if (resetFilters && !result?.filters) {
        setOriginalFilters(null);
      }

      if (skip === undefined) {
        setSearchResult(result);
      } else {
        setSearchResult((prev) => ({
          ...result,
          data: [...(prev?.data ?? []), ...result.data],
        }));
      }
      setLoading(false);
    },
    [slug]
  );

  const handleFilterChange = useCallback(
    (key: keyof FilterOptions, value: string) => {
      const updatedFilters: FilterOptions = { ...filters };
      if (!updatedFilters[key]) {
        updatedFilters[key] = [];
      }

      if (updatedFilters[key].includes(value)) {
        updatedFilters[key] = updatedFilters[key].filter((v) => v !== value);
      } else {
        updatedFilters[key].push(value);
      }

      setFilters(updatedFilters);

      const filterParams = new URLSearchParams();

      if (currentSearchTerm?.trim()) {
        filterParams.set("term", currentSearchTerm);
      }

      for (const [filterKey, filterValues] of Object.entries(updatedFilters)) {
        for (const filterValue of filterValues) {
          filterParams.append(filterKey, filterValue);
        }
      }

      window.history.replaceState(
        null,
        "",
        `/${slug}?${filterParams.toString()}`
      );

      search(currentSearchTerm ?? "", updatedFilters);
    },
    [filters, currentSearchTerm, search, slug]
  );

  // Load search results when the search term is populated on page load
  useEffect(() => {
    if (shouldLoad) {
      search(searchTerm ?? "", filters, true);
      setShouldLoad(false);
    }
  }, [searchParams, searchTerm, filters, search, shouldLoad]);

  return (
    <SearchContext.Provider
      value={{
        slug,
        searchTerm,
        setSearchTerm,
        filters,
        searchResult,
        setSearchResult,
        search,
        currentSearchTerm,
        loading,
        handleFilterChange,
        originalFilters,
        resetFilters,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
}

export function useSearch() {
  const context = useContext(SearchContext);
  if (undefined === context) {
    throw new Error("useSearch must be used within a SearchProvider");
  }
  return context;
}
